import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCopyToClipboard } from "react-use";
import { CancelCrossIcon, Dialog, Dot, Header3, IconButton } from "@activecollab/components";
import PropTypes from "prop-types";
import { CopyField } from "~/components/CopyToClipboard/CopyField";
import { showErrorToast, showSuccessToast } from "~/components/Toast";

export const TokenDialog = ({ open, onClose, urlLabel, urlValue, tokenLabel, tokenValue, isFetching, children }) => {
  const { t } = useTranslation();
  const [copyState, copyToClipboard] = useCopyToClipboard();
  const [lastCopiedType, setLastCopiedType] = useState(null);

  const handleCopyToClipboard = useCallback(
    (value, type) => {
      copyToClipboard(value);
      setLastCopiedType(type);
    },
    [copyToClipboard]
  );

  const showToastOnCopy = useCallback(() => {
    if (copyState.value) {
      showSuccessToast(t(`${lastCopiedType} copied to clipboard.`));
    }
    if (copyState.error) {
      showErrorToast(t(copyState.error.message));
    }
  }, [copyState.error, copyState.value, lastCopiedType, t]);

  useEffect(() => {
    showToastOnCopy();
  }, [showToastOnCopy]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClickOutside={onClose}
      className="md:tw-w-[700px]"
    >
      <Dialog.Title
        disableDefaultHeading
        className="tw-flex tw-justify-between tw-items-center"
      >
        <Header3>{t("Use Token")}</Header3>
        <IconButton
          onClick={onClose}
          variant="text gray"
        >
          <CancelCrossIcon />
        </IconButton>
      </Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <CopyField
            label={urlLabel}
            value={urlValue}
            onCopy={handleCopyToClipboard}
            isLoading={isFetching}
          />
          <CopyField
            label={tokenLabel}
            value={tokenValue}
            renderValue={(token) => (
              <>
                {token?.slice(0, 5)}
                {[...Array(22)].map((_, index) => (
                  <Dot
                    data-testid="dot"
                    key={`dot-${index}`}
                    size={8}
                    className="tw-ml-1 tw-mt-0.5 tw-shrink-0"
                    color="var(--color-theme-900)"
                  />
                ))}
              </>
            )}
            onCopy={handleCopyToClipboard}
            isLoading={isFetching}
          />
          {children}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

TokenDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  urlLabel: PropTypes.string,
  urlValue: PropTypes.string,
  tokenLabel: PropTypes.string,
  tokenValue: PropTypes.string,
  isFetching: PropTypes.bool,
  children: PropTypes.node,
};
